// src/contexts/bf-account-context.tsx
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { BFAccount } from "@/types/black-friday";

const LOCALSTORAGE_KEY = "bf_accounts";
const CURRENT_ACCOUNT_KEY = "bf_current_account_id";
// Note: We no longer sync to Clerk metadata due to storage limits (5 KB max)
// and the large size of Google Apps Script Web App URLs (~200-300 chars each)

interface BFAccountContextType {
  accounts: BFAccount[];
  currentAccount: BFAccount | null;
  isLoading: boolean;
  addAccount: (accountCID: string, accountName: string, webAppUrl: string) => Promise<void>;
  removeAccount: (accountId: string) => Promise<void>;
  switchAccount: (accountId: string) => void;
  updateAccountTimestamp: (accountId: string) => Promise<void>;
  updateAccount: (accountId: string, updates: Partial<BFAccount>) => Promise<void>;
}

const BFAccountContext = createContext<BFAccountContextType | undefined>(
  undefined
);

export function BFAccountProvider({ children }: { children: React.ReactNode }) {
  const [accounts, setAccounts] = useState<BFAccount[]>([]);
  const [currentAccount, setCurrentAccount] = useState<BFAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load accounts on mount
  useEffect(() => {
    loadAccounts();
  }, []); // Load once on mount

  // NOTE: We no longer sync BF accounts to Clerk metadata due to storage limits
  // Google Apps Script URLs are very long (200-300 chars each), causing us to hit
  // the 5 KB unsafeMetadata limit with just 2-3 accounts. BF accounts are device-specific
  // anyway since they require Google Sheets setup, so cross-device sync isn't critical.

  // Load current account when accounts change
  useEffect(() => {
    if (accounts.length > 0) {
      loadCurrentAccount();
    } else {
      setCurrentAccount(null);
    }
  }, [accounts]);

  async function loadAccounts() {
    try {
      let loadedAccounts: BFAccount[] = [];

      // Load from localStorage only (no Clerk sync due to storage limits)
      const localAccounts = localStorage.getItem(LOCALSTORAGE_KEY);
      if (localAccounts) {
        try {
          const parsed = JSON.parse(localAccounts);
          if (Array.isArray(parsed)) {
            loadedAccounts = parsed;
          }
        } catch (e) {
          console.error("Failed to parse BF accounts from localStorage:", e);
        }
      }

      // Deduplicate accounts (keep first occurrence)
      const deduped = deduplicateAccounts(loadedAccounts);

      // If we removed duplicates, save the cleaned list
      if (deduped.length !== loadedAccounts.length) {
        console.log(`Removed ${loadedAccounts.length - deduped.length} duplicate accounts`);
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(deduped));
        loadedAccounts = deduped;
      }

      setAccounts(loadedAccounts);
    } catch (error) {
      console.error("Error loading BF accounts:", error);
    } finally {
      setIsLoading(false);
    }
  }

  // Helper function to remove duplicates based on CID (primary) or name+URL (fallback)
  function deduplicateAccounts(accounts: BFAccount[]): BFAccount[] {
    const seen = new Set<string>();
    const unique: BFAccount[] = [];

    for (const account of accounts) {
      // Use CID as primary unique identifier, fallback to name+URL for legacy accounts
      const key = account.accountCID || `${account.accountName}|${account.webAppUrl}`;

      if (!seen.has(key)) {
        seen.add(key);
        unique.push(account);
      }
    }

    return unique;
  }

  function loadCurrentAccount() {
    const currentId = localStorage.getItem(CURRENT_ACCOUNT_KEY);

    if (currentId) {
      const account = accounts.find((a) => a.id === currentId);
      if (account) {
        setCurrentAccount(account);
        return;
      }
    }

    // Default to most recently updated account
    const sorted = [...accounts].sort((a, b) => {
      const aTime = a.lastUpdated || a.createdAt;
      const bTime = b.lastUpdated || b.createdAt;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

    if (sorted.length > 0) {
      setCurrentAccount(sorted[0]);
      localStorage.setItem(CURRENT_ACCOUNT_KEY, sorted[0].id);
    }
  }

  const saveAccounts = useCallback(async (newAccounts: BFAccount[]) => {
    // Save to localStorage only (no Clerk sync due to storage limits)
    // The Google Apps Script Web App URLs are very long (200-300 chars each),
    // and with main app data already using ~2.7 KB of the 5 KB unsafeMetadata limit,
    // we cannot afford to store BF accounts there as well.
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(newAccounts));
  }, []);

  const addAccount = useCallback(async (accountCID: string, accountName: string, webAppUrl: string) => {
    console.log('[BF Context] addAccount called:', { accountCID, accountName, webAppUrl });

    setAccounts((prevAccounts) => {
      console.log('[BF Context] Current accounts before add:', prevAccounts.length);

      // Check for duplicates based on CID (primary) or name/URL (fallback)
      const duplicate = prevAccounts.find(
        (acc) =>
          (acc.accountCID && acc.accountCID === accountCID) || // Check CID first
          acc.accountName === accountName ||
          acc.webAppUrl === webAppUrl
      );

      if (duplicate) {
        console.warn(`Account "${accountName}" (CID: ${accountCID}) already exists, switching to it instead`);

        // If the existing account doesn't have a CID but new data does, update it
        if (!duplicate.accountCID && accountCID) {
          const updatedAccount = { ...duplicate, accountCID };
          const updatedAccounts = prevAccounts.map(acc =>
            acc.id === duplicate.id ? updatedAccount : acc
          );
          saveAccounts(updatedAccounts);
          setCurrentAccount(updatedAccount);
          localStorage.setItem(CURRENT_ACCOUNT_KEY, duplicate.id);
          return updatedAccounts;
        } else {
          // Just switch to existing account
          setCurrentAccount(duplicate);
          localStorage.setItem(CURRENT_ACCOUNT_KEY, duplicate.id);
          return prevAccounts; // No change to accounts array
        }
      }

      const newAccount: BFAccount = {
        id: accountCID, // Use CID as the ID
        accountCID,
        accountName,
        webAppUrl,
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString(),
      };

      const updated = [...prevAccounts, newAccount];
      console.log('[BF Context] New account list. Count:', updated.length);
      console.log('[BF Context] New account:', newAccount);

      // Save to localStorage
      saveAccounts(updated);

      // Auto-select the new account
      setCurrentAccount(newAccount);
      localStorage.setItem(CURRENT_ACCOUNT_KEY, newAccount.id);
      console.log('[BF Context] Account added successfully');

      return updated;
    });
  }, [saveAccounts]);

  const removeAccount = useCallback(async (accountId: string) => {
    setAccounts((prevAccounts) => {
      const updated = prevAccounts.filter((a) => a.id !== accountId);
      saveAccounts(updated);
      return updated;
    });

    // If we removed the current account, clear it
    setCurrentAccount((prevCurrent) => {
      if (prevCurrent?.id === accountId) {
        localStorage.removeItem(CURRENT_ACCOUNT_KEY);
        return null;
      }
      return prevCurrent;
    });
  }, [saveAccounts]);

  const switchAccount = useCallback((accountId: string) => {
    setAccounts((prevAccounts) => {
      const account = prevAccounts.find((a) => a.id === accountId);
      if (account) {
        setCurrentAccount(account);
        localStorage.setItem(CURRENT_ACCOUNT_KEY, accountId);
      }
      return prevAccounts; // No change to accounts array
    });
  }, []);

  const updateAccountTimestamp = useCallback(async (accountId: string) => {
    setAccounts((prevAccounts) => {
      const updated = prevAccounts.map((a) =>
        a.id === accountId
          ? { ...a, lastUpdated: new Date().toISOString() }
          : a
      );
      saveAccounts(updated);
      return updated;
    });
  }, [saveAccounts]);

  const updateAccount = useCallback(async (accountId: string, updates: Partial<BFAccount>) => {
    setAccounts((prevAccounts) => {
      const updated = prevAccounts.map((a) =>
        a.id === accountId
          ? { ...a, ...updates, lastUpdated: new Date().toISOString() }
          : a
      );
      saveAccounts(updated);
      return updated;
    });
  }, [saveAccounts]);

  const contextValue = useMemo(
    () => ({
      accounts,
      currentAccount,
      isLoading,
      addAccount,
      removeAccount,
      switchAccount,
      updateAccountTimestamp,
      updateAccount,
    }),
    [accounts, currentAccount, isLoading, addAccount, removeAccount, switchAccount, updateAccountTimestamp, updateAccount]
  );

  return (
    <BFAccountContext.Provider value={contextValue}>
      {children}
    </BFAccountContext.Provider>
  );
}

export function useBFAccounts() {
  const context = useContext(BFAccountContext);
  if (context === undefined) {
    throw new Error("useBFAccounts must be used within a BFAccountProvider");
  }
  return context;
}
